/**
 * Contact forms — native select switches which Shopify Forms app panel is shown.
 *
 * Options are built from panel data-label attributes (theme-block titles).
 * Hidden panels are inert so only the visible form can be focused or submitted.
 * Design mode: shopify:block:select reveals the matching panel.
 * Deep link: ?enquiry=<handleized-title>
 */
(() => {
  const TAG = 'contact-forms';
  const FORM_EMBED_TAG = 'shopify-forms-embed';
  const FORM_BUTTON_STYLE_ATTR = 'data-contact-form-button-style';
  const FORM_BUTTON_SELECTOR = '[data-testid="btn-form-submit"]';
  const FORM_BUTTON_CSS = `
    [data-testid="form"] ${FORM_BUTTON_SELECTOR} {
      display: inline-flex;
      box-sizing: border-box;
      align-items: center;
      align-self: start;
      justify-content: center;
      width: fit-content;
      min-width: 0;
      max-width: 100%;
      min-height: var(--size-8, 32px);
      margin: 0;
      /* The theme button's 1px transparent border is folded into the padding instead.
         Forms zeroes border-width on hover with a rule we can't outrank, which shrank
         the button by 2px the moment the pointer landed on it */
      padding: calc(var(--spacing-3xs, 8px) + 1px) calc(var(--spacing-xs, 16px) + 1px);
      border: 0;
      border-radius: var(--radius-full, 360px);
      background-color: var(--surface-brand-dark, #4d9d34);
      color: var(--text-invert, #fff);
      box-shadow: none;
      font-family: var(--font-subheading--family, inherit);
      font-size: var(--font-button-sm-size, 12px);
      font-weight: var(--font-button-sm-weight, 600);
      line-height: var(--font-button-sm-line-height, 1.4);
      letter-spacing: var(--font-button-sm-letter-spacing, 0);
      text-align: center;
      white-space: nowrap;
      cursor: pointer;
      appearance: none;
      transform: none !important;
      translate: none !important;
      transition:
        background-color 0.2s ease,
        color 0.2s ease,
        opacity 0.2s ease;
    }

    [data-testid="form"] ${FORM_BUTTON_SELECTOR}:hover,
    [data-testid="form"] ${FORM_BUTTON_SELECTOR}:active {
      background-color: var(--surface-brand-dark, #4d9d34);
      color: var(--text-invert, #fff);
      transform: none !important;
      translate: none !important;
    }

    [data-testid="form"] ${FORM_BUTTON_SELECTOR}:focus-visible {
      background-color: var(--surface-brand-dark, #4d9d34);
      color: var(--text-invert, #fff);
      outline: 2px solid var(--border-brand-dark, #4d9d34);
      outline-offset: 2px;
      transform: none !important;
      translate: none !important;
    }

    [data-testid="form"] ${FORM_BUTTON_SELECTOR}[aria-disabled='true'] {
      opacity: 0.3;
      cursor: not-allowed;
    }
  `;

  const whenFormsDefined = () => {
    if (!window.customElements?.whenDefined) return Promise.resolve();
    if (window.customElements.get(FORM_EMBED_TAG)) return Promise.resolve();

    return window.customElements.whenDefined(FORM_EMBED_TAG);
  };

  class ContactForms extends HTMLElement {
    #select = null;
    #control = null;
    #panels = [];
    #formObserver = null;
    #shadowObservers = new Map();
    #waitingEmbeds = new Set();

    connectedCallback() {
      this.#select = this.querySelector('[data-contact-forms-select]');
      this.#control = this.querySelector('[data-contact-forms-control]');
      this.#panels = Array.from(this.querySelectorAll('[data-contact-forms-panel]'));

      if (this.#panels.length === 0) return;

      this.#buildSelect();
      this.#bindEvents();
      this.#activate(this.#initialId());
      this.#styleFormButtons();
      this.#observeFormEmbeds();
      this.classList.add('is-ready');

      // Shadow attach does not mutate light DOM, so #formObserver never sees the upgrade.
      // A Forms script slower than the poll window would otherwise leave the CTAs on the
      // app's own colours, so re-run once the element is actually defined.
      whenFormsDefined().then(() => {
        if (this.isConnected) this.#styleFormButtons();
      });
    }

    disconnectedCallback() {
      if (this.#select) {
        this.#select.removeEventListener('change', this.#handleChange);
      }

      document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
      this.#formObserver?.disconnect();

      for (const observer of this.#shadowObservers.values()) {
        observer.disconnect();
      }

      this.#shadowObservers.clear();
    }

    get #isDesignMode() {
      return this.dataset.designMode === 'true';
    }

    #visiblePanels() {
      if (this.#isDesignMode) return this.#panels;

      return this.#panels.filter((panel) => panel.dataset.empty !== 'true');
    }

    #buildSelect() {
      if (!this.#select) return;

      const visible = this.#visiblePanels();

      this.#select.replaceChildren();

      for (const panel of visible) {
        const option = document.createElement('option');
        const blockId = panel.getAttribute('data-block-id') || '';

        option.value = blockId;
        option.textContent = panel.getAttribute('data-label') || '';
        this.#select.append(option);
      }

      if (this.#control) {
        this.#control.hidden = visible.length < 2 && !this.#isDesignMode;
      }
    }

    #initialId() {
      const enquiry = new URLSearchParams(window.location.search).get('enquiry');

      if (enquiry) {
        const match = this.#visiblePanels().find((panel) => panel.dataset.handle === enquiry);

        if (match) return match.getAttribute('data-block-id');
      }

      const first = this.#visiblePanels()[0];

      return first ? first.getAttribute('data-block-id') : null;
    }

    #bindEvents() {
      if (this.#select) {
        this.#select.addEventListener('change', this.#handleChange);
      }

      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    }

    #handleChange = () => {
      if (!this.#select) return;

      this.#activate(this.#select.value);
    };

    #handleBlockSelect = (event) => {
      if (!this.#isDesignMode || !event.target || !this.contains(event.target)) return;

      const panel = event.target.closest('[data-contact-forms-panel]');

      if (!panel || !this.contains(panel)) return;

      this.#activate(panel.getAttribute('data-block-id'));
    };

    #activate(blockId) {
      if (!blockId) return;

      for (const panel of this.#panels) {
        const isActive = panel.getAttribute('data-block-id') === blockId;
        const isEmpty = panel.dataset.empty === 'true';

        if (!this.#isDesignMode && isEmpty) {
          panel.hidden = true;
          panel.inert = true;
          panel.classList.remove('is-active');
          continue;
        }

        panel.hidden = !isActive;
        panel.inert = !isActive;
        panel.classList.toggle('is-active', isActive);
      }

      if (this.#select && this.#select.value !== blockId) {
        this.#select.value = blockId;
      }

      // A Forms embed that loaded inside a hidden panel measured zero width,
      // so it needs a resize to lay out once revealed.
      window.dispatchEvent(new Event('resize'));
    }

    #ensureButtonStyle(shadowRoot) {
      let style = shadowRoot.querySelector(`[${FORM_BUTTON_STYLE_ATTR}]`);

      if (!style) {
        style = document.createElement('style');
        style.setAttribute(FORM_BUTTON_STYLE_ATTR, '');
        shadowRoot.appendChild(style);
      }

      if (style.textContent !== FORM_BUTTON_CSS) {
        style.textContent = FORM_BUTTON_CSS;
      }
    }

    #attachButtonStyle(embed) {
      const shadowRoot = embed.shadowRoot;
      if (!shadowRoot) return false;

      this.#ensureButtonStyle(shadowRoot);

      if (!this.#shadowObservers.has(embed)) {
        const observer = new MutationObserver(() => {
          this.#ensureButtonStyle(shadowRoot);
        });

        observer.observe(shadowRoot, { childList: true, subtree: true });
        this.#shadowObservers.set(embed, observer);
      }

      return true;
    }

    #waitForShadowRoot(embed) {
      if (this.#waitingEmbeds.has(embed)) return;

      this.#waitingEmbeds.add(embed);
      const maxAttempts = 80;
      let attempts = 0;

      const poll = () => {
        if (!this.isConnected || this.#attachButtonStyle(embed) || attempts >= maxAttempts) {
          this.#waitingEmbeds.delete(embed);
          return;
        }

        attempts += 1;
        window.setTimeout(poll, 50);
      };

      poll();
    }

    #styleFormButtons() {
      for (const embed of this.querySelectorAll(FORM_EMBED_TAG)) {
        if (this.#attachButtonStyle(embed)) continue;

        this.#waitForShadowRoot(embed);
      }
    }

    #observeFormEmbeds() {
      if (this.#formObserver) return;

      this.#formObserver = new MutationObserver(() => {
        this.#styleFormButtons();
      });

      this.#formObserver.observe(this, { childList: true, subtree: true });
    }
  }

  if (!customElements.get(TAG)) {
    customElements.define(TAG, ContactForms);
  }
})();
